import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { useStore } from 'core/store';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { PeopleDetails } from 'offer/components/people-details';
import { Settings } from 'offer/components/settings';
import { HomeOffer } from 'common/components/home';
import { AutoOffer } from 'common/components/auto';
import { CondoOfferDetails } from 'common/components/condo';
import { RentersOffer } from 'common/components/renters';
import { FarmOffer } from 'common/components/farm';
import { PersonalInjuryProtection } from 'common/components/personal-injury-protection';
import { PolicyPickerCard } from 'offer/components/sidebar/components/policy-picker-card';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';
import { QUOTE_STEPS } from '../step-rail/step-rail';
import useStyles from './quote-workspace.styles';

const QuoteWorkspace = observer(({ footer }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const { values } = useFormikContext();
  const step = store.quoteStep;
  const section = step === 2 ? 'details' : 'coverage';
  const billingDetails = useRef(null);
  useEffect(() => {
    if (store.scrollTo === 'policySettings' && billingDetails.current) {
      billingDetails.current.open = true;
    }
  }, [store.scrollTo, step]);
  const products = [
    store.shouldShowHomeTab && ['Home', HomeOffer],
    store.shouldShowCondoTab && ['Condo', CondoOfferDetails],
    store.shouldShowAutoTab && ['Auto', AutoOffer],
    store.shouldShowRentersTab && ['Renters', RentersOffer],
    store.shouldShowFarmTab && [getFarmPolicyMask(store.selectedOption, store.state)?.tabLabel || 'Farm', FarmOffer]
  ].filter(Boolean);
  const count =
    step === 1
      ? `${values.drivers?.length || 0} ${values.drivers?.length === 1 ? 'person' : 'people'}`
      : `${products.length} ${products.length === 1 ? 'product' : 'products'}`;

  if (step === 4) {
    return (
      <div className={classes.review}>
        {footer}
        <details className={classes.disclosure} ref={billingDetails}>
          <summary>Billing & policy settings</summary>
          <div className={classes.forms}>
            <Settings />
          </div>
        </details>
      </div>
    );
  }

  return (
    <Card className={classes.card}>
      <div className={classes.header}>
        <Label type="body1" component="h2" className={classes.heading}>
          {QUOTE_STEPS[step - 1].title}
        </Label>
        <Label type="body2">{count}</Label>
      </div>
      <div className={classes.body}>
        {step === 1 ? (
          <>
            <div className={classes.forms}>
              <PeopleDetails compact hasAuto={store.shouldShowAutoTab} />
            </div>
            <details className={classes.disclosure}>
              <summary>Products</summary>
              <PolicyPickerCard />
            </details>
          </>
        ) : (
          <>
            <Label type="body2" component="p" className={classes.intro}>
              {step === 2
                ? 'Review the details on this quote. Open an item to make a correction.'
                : 'Choose limits and deductibles. Select Update price to apply your changes.'}
            </Label>
            {products.map(([title, Component]) => (
              <div className={classes.product} key={title}>
                <Label type="body1" component="h3" className={classes.productTitle}>
                  {title}
                </Label>
                {Component === FarmOffer && step === 3 && (
                  <Label type="body2" component="p" className={classes.intro}>
                    Property details and coverages are grouped by location.
                  </Label>
                )}
                <div className={classes.forms}>
                  <Component
                    section={Component === AutoOffer && store.quoteShowAllCarFields ? 'all' : section}
                    hasAuto={store.shouldShowAutoTab}
                  />
                </div>
              </div>
            ))}
            {step === 3 && store.shouldShowPIPTab && (
              <div className={classes.forms}>
                <PersonalInjuryProtection />
              </div>
            )}
            {!products.length && <Label type="body2">No product details are available for this quote.</Label>}
          </>
        )}
        <div className={classes.actions}>
          {footer}
          <Button variant="contained" color="primary" onClick={() => store.setQuoteStep(step + 1)}>
            {step === 3 ? 'Continue to review' : 'Continue'}
          </Button>
        </div>
      </div>
    </Card>
  );
});
QuoteWorkspace.propTypes = { footer: PropTypes.node };
export default QuoteWorkspace;
